import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { 
  LayoutDashboard, 
  BookOpen, 
  Trophy, 
  LogOut,
  Users,
  ChevronRight,
  Crown,
  Award,
  Gift,
  Lightbulb,
  Menu,
  X
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

export const Layout = ({ children }) => {
  const { user, logout, isAdmin, isSupervisor } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const navItems = [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/sops", icon: BookOpen, label: "Training" },
    { to: "/certificates", icon: Award, label: "Certificates" },
    { to: "/benefits", icon: Gift, label: "Benefits" },
    { to: "/development", icon: Lightbulb, label: "Development" },
    { to: "/progress", icon: Trophy, label: "Progress" },
  ];

  const canViewTeam = isAdmin || isSupervisor;

  if (canViewTeam) {
    navItems.push({ to: "/admin", icon: Users, label: "Team" });
  }

  const getInitials = (name) => {
    return name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "U";
  };

  return (
    <div className="min-h-screen bg-[var(--rd-cream)]">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 bg-white border-r border-[hsl(var(--border))]">
        {/* Logo */}
        <div className="flex items-center gap-3 p-5 border-b border-[hsl(var(--border))]">
          <img
            src="https://customer-assets.emergentagent.com/job_clubhouse-standards/artifacts/tsx8cmf7_CMYK_RD%20small.jpg"
            alt="Royal Dornoch"
            className="w-16 h-16 object-contain"
          />
          <div>
            <h1 className="text-sm font-semibold text-[var(--rd-green)]" style={{ fontFamily: "'Cinzel', serif" }}>
              Royal Dornoch
            </h1>
            <p className="text-[10px] text-[hsl(var(--muted-foreground))] uppercase tracking-widest">
              Training Portal
            </p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `nav-link ${isActive ? "active" : ""}`
              }
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-[hsl(var(--border))]">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 p-3 rounded-sm hover:bg-[hsl(var(--secondary))] transition-colors" data-testid="user-menu-trigger">
                <Avatar className="h-10 w-10 border border-[var(--rd-gold)]">
                  <AvatarFallback className="bg-[var(--rd-green)] text-white text-sm font-medium">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-[hsl(var(--foreground))]">{user?.name}</p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))] capitalize">{user?.role}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="mr-2 h-4 w-4" />
                <span>Sign out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-[hsl(var(--border))] z-40 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <img
            src="https://customer-assets.emergentagent.com/job_clubhouse-standards/artifacts/tsx8cmf7_CMYK_RD%20small.jpg"
            alt="Royal Dornoch"
            className="w-12 h-12 object-contain"
          />
          <span className="font-semibold text-[var(--rd-green)] text-sm" style={{ fontFamily: "'Cinzel', serif" }}>
            Royal Dornoch
          </span>
        </div>
        <div className="flex items-center gap-3">
          {/* Hamburger Menu for all nav items */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-2" data-testid="mobile-menu-btn">
                <Menu className="w-6 h-6 text-[var(--rd-green)]" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Navigation</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {navItems.map((item) => (
                <DropdownMenuItem key={item.to} onClick={() => navigate(item.to)}>
                  <item.icon className="mr-2 h-4 w-4" />
                  <span>{item.label}</span>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                <span>Sign out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {/* User Avatar */}
          <Avatar className="h-8 w-8 border border-[var(--rd-gold)]">
            <AvatarFallback className="bg-[var(--rd-green)] text-white text-xs">
              {getInitials(user?.name)}
            </AvatarFallback>
          </Avatar>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:pl-64 pt-16 lg:pt-0 pb-20 lg:pb-0">
        <div className="p-4 lg:p-8">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="bottom-nav">
        {navItems.slice(0, 4).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `bottom-nav-item ${isActive ? "active" : ""}`
            }
            data-testid={`mobile-nav-${item.label.toLowerCase()}`}
          >
            <item.icon className="w-5 h-5" />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
};

export default Layout;
